import { FC } from "react";
import { FieldInputProps } from "react-final-form";
import { useSelector } from "react-redux";

import { Error } from "@livechat/design-system-icons";
import { IPickerListItem, Icon, Picker } from "@livechat/design-system-react-components";

import { mapTagsToOptionsWithGrouping } from "helpers/tags";
import { shortPickerStyles } from "routes/automations/shared/styles";
import { IRuleTriggerTagAdded, IRuleTriggerTagID, IRuleTriggerTagRemoved } from "store/entities/rules/interfaces";
import { ITag, selectTags, selectTagsAlphabetically } from "store/entities/tags";
import { selectTeamsAlphabetically } from "store/entities/teams";

import { changeTrigger, changeTriggerValues } from "../utils";

interface IProps {
  input: FieldInputProps<IRuleTriggerTagID | IRuleTriggerTagAdded | IRuleTriggerTagRemoved>;
}

export const TagTrigger: FC<IProps> = ({ input }) => {
  const tags = useSelector(selectTagsAlphabetically);
  const tagsById = useSelector(selectTags);
  const teams = useSelector(selectTeamsAlphabetically);

  const equalityOptions = [
    createEqualityOption("is"),
    createEqualityOption("isNot"),
    createEqualityOption("hasBeenAdded"),
    createEqualityOption("hasBeenRemoved"),
  ];

  const selectedEqualityOptions = [
    createEqualityOption(
      ((): "is" | "isNot" | "hasBeenAdded" | "hasBeenRemoved" => {
        switch (input.value.type) {
          case "tagID":
            return input.value.invert ? "isNot" : "is";
          case "tagAdded":
            return "hasBeenAdded";
          case "tagRemoved":
            return "hasBeenRemoved";
        }
      })()
    ),
  ];

  const handleSelectEqualityOption = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      return;
    }

    const selectedOption = selectedOptions[0];

    const selectedTags = input.value.in;

    switch (selectedOption.key) {
      case "is": {
        changeTrigger(input, { type: "tagID", in: selectedTags, invert: false });
        break;
      }
      case "isNot": {
        changeTrigger(input, { type: "tagID", in: selectedTags, invert: true });
        break;
      }
      case "hasBeenAdded":
        changeTrigger(input, { type: "tagAdded", in: selectedTags });
        break;
      case "hasBeenRemoved":
        changeTrigger(input, { type: "tagRemoved", in: selectedTags });
        break;
    }
  };

  const tagOptions = mapTagsToOptionsWithGrouping(tags, teams);

  const selectedTagOptions = input.value.in.map(tagId => {
    const tag = tagsById[tagId];
    return tag ? createTagOption(tag) : createTagRemovedOption(tagId);
  });

  const handleSelectTagOption = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      changeTriggerValues(input, { in: [] });
      return;
    }

    changeTriggerValues(input, { in: selectedOptions.map(option => option.key) });
  };

  return (
    <>
      <Picker
        options={equalityOptions}
        selected={selectedEqualityOptions}
        onSelect={handleSelectEqualityOption}
        hideClearButton
        searchDisabled
        css={shortPickerStyles}
      />
      <Picker
        type="multi"
        options={tagOptions}
        selected={selectedTagOptions}
        onSelect={handleSelectTagOption}
        placeholder="Select one tag or more..."
      />
    </>
  );
};

const equalityName = (key: "is" | "isNot" | "hasBeenAdded" | "hasBeenRemoved"): string => {
  switch (key) {
    case "is":
      return "is";
    case "isNot":
      return "is not";
    case "hasBeenAdded":
      return "has been added";
    case "hasBeenRemoved":
      return "has been removed";
  }
};

const createEqualityOption = (key: "is" | "isNot" | "hasBeenAdded" | "hasBeenRemoved"): IPickerListItem => ({
  key,
  name: equalityName(key),
});

const createTagOption = (tag: ITag): IPickerListItem => ({
  key: tag.id,
  name: tag.name,
});

const createTagRemovedOption = (tagId: string): IPickerListItem => ({
  key: tagId,
  name: "Tag removed",
  selectedTagOptions: {
    kind: "error",
    leftNode: <Icon source={Error} />,
  },
});
